import { isSolidSlabFloor } from "building-generation/elements/floor/floor";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { BuildingEntityType } from "building-types/entities/buildingEntityTypeTypes";
import type { FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import type { FloorSystem } from "building-types/floors/floorSystemTypes";
import { FloorType } from "building-types/floors/floorTypes";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { FloorBayHandle, DocEntityHandle, FloorSystemHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  GeneratedFloorBaseAssembly,
  GeneratedFramedFloorAssembly,
  GeneratedSlabFloorAssembly,
} from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { Table } from "common/data/table";
import { MS_Color__C } from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import { getFloorIdForBay } from "doc/elements/floor/floorAssembly";
import type { Extrusion } from "geom/brep";
import { extrusionToBRep } from "geom/extrude";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2Centroid, polyLine2SetCW, polyLine2SetCCW } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { UVGeneration } from "geom/uv";
import { processFloorExtrusions } from "../floors/processFloorExtrusions";
import { buildExteriorLayers, buildInteriorLayers } from "./buildingGeometryTools";
import type { FloorRoomRegion } from "./floorReportingUnitAttribution";
import { floorRegionForPoint, floorRoomRegions } from "./floorReportingUnitAttribution";

/**
 * GPU pick hierarchy for horizontal-assembly subfloor/slab meshes is floor system, then bay.
 * Only doc bays (present in floor splitData) get a bay pick id — synthetic generation bays fall
 * through to the parent floor system so the mesh stays selectable.
 */
function bayHandleFromAssembly(
  assembly: GeneratedFloorBaseAssembly,
  floors: Table<FloorAssemblyOverride> | undefined,
): FloorBayHandle | undefined {
  if (assembly.bayId && floors && getFloorIdForBay(floors, assembly.bayId)) {
    return {
      type: "FLOOR_BAY",
      id: assembly.bayId,
    };
  }
  return undefined;
}

/** The entity a click on a horizontal-assembly mesh should select. */
function horizontalAssemblyPickHandle(
  assembly: { readonly docEntityHandle?: DocEntityHandle },
  bayHandle: FloorBayHandle | undefined,
  floorSystemId: string,
): DocEntityHandle {
  if (assembly.docEntityHandle?.type === "COMPONENT_INSTANCE") {
    return assembly.docEntityHandle;
  }
  if (bayHandle) {
    return bayHandle;
  }
  const floorSystemHandle: FloorSystemHandle = { type: "FLOOR_SYSTEM", id: floorSystemId };
  return floorSystemHandle;
}

/** The generated entity a floor's own slab/subfloor geometry selects, which has no doc entity. */
function floorPickHandle(floor: { readonly entityType: BuildingEntityType; readonly id: string }): DocEntityHandle {
  return { type: "GENERATED_ENTITY", entity: { entityType: floor.entityType, id: floor.id } as BuildingEntityHandle };
}

function isExteriorRoomClass(roomClass: RoomClass): boolean {
  switch (roomClass) {
    case RoomClass.EXTERIOR:
    case RoomClass.PORCH:
    case RoomClass.PATIO:
    case RoomClass.DECK:
      return true;
    default:
      return false;
  }
}

/**
 * The room a floor/subfloor extrusion covers, resolved from its footprint centroid. Floor assemblies
 * are grouped by construction parameters, not by unit or by component instance, so one assembly can
 * span both; resolving per extrusion keeps each surface with the room it sits in. Undefined when the
 * extrusion has no usable footprint or the level has no rooms.
 */
function floorExtrusionRegion(extrusion: Extrusion, regions: readonly FloorRoomRegion[]): FloorRoomRegion | undefined {
  const outline = extrusion.profile.loops[0];
  if (!outline || outline.length < 6) {
    return undefined;
  }
  // polyLine2Centroid returns a shared buffer; read it before the next call overwrites it.
  const centroid = polyLine2Centroid(outline);
  if (!centroid) {
    return undefined;
  }
  const [x, y] = centroid;
  return floorRegionForPoint(x, y, regions);
}

/** Reporting-unit handle for a floor/subfloor extrusion, or `fallbackHandle` when it resolves to no room. */
function extrusionReportingUnitHandle(
  extrusion: Extrusion,
  regions: readonly FloorRoomRegion[],
  fallbackHandle: DocEntityHandle | undefined,
): DocEntityHandle | undefined {
  const region = floorExtrusionRegion(extrusion, regions);
  return region ? region.reportingUnitHandle : fallbackHandle;
}

/**
 * A floor surface inside a component instance is selected as the instance, so it highlights with the
 * rest of that instance's content; elsewhere it selects the generated floor it belongs to.
 */
function floorExtrusionPickHandle(
  extrusion: Extrusion,
  regions: readonly FloorRoomRegion[],
  floor: { readonly entityType: BuildingEntityType; readonly id: string },
): DocEntityHandle {
  return floorExtrusionRegion(extrusion, regions)?.componentInstanceHandle ?? floorPickHandle(floor);
}

export function getSlabFlooringGeometries(
  floorSystems: Table<FloorSystem>,
  slabFloorAssemblies: Table<GeneratedSlabFloorAssembly>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  emitter: BuildingGeometryEmitter,
  floors?: Table<FloorAssemblyOverride>,
) {
  const materialSrc = {
    ...MS_Color__C,
    colorId: DMC.SystemColors.Light_Cream,
  };

  for (const levelId in floorSystems) {
    const roomRegions = floorRoomRegions(levelId, rooms, roomsByLevel);
    const floorSystem = floorSystems[levelId];
    for (const [roomClass, roomClassFloors] of floorSystem.roomClassFloors.entries()) {
      for (const floor of roomClassFloors) {
        if (!isSolidSlabFloor(floor)) {
          continue;
        }
        const { slab: slabFloorExtrusions } = floor;
        const editedSlabFloorExtrusions = processFloorExtrusions(slabFloorExtrusions);

        const threeLayers = isExteriorRoomClass(roomClass) ? buildExteriorLayers() : buildInteriorLayers();
        editedSlabFloorExtrusions.forEach((editedSlabFloorExtrusion) =>
          emitter.emit({
            geom: extrusionToBRep(editedSlabFloorExtrusion),
            faceCategory: FaceCategory.SLAB_FLOORING,
            threeLayers,
            levelId,
            materialSlot: "slabfloor",
            materialSrc,
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            pickHandle: floorExtrusionPickHandle(editedSlabFloorExtrusion, roomRegions, floor),
            entityHandleForSelectingReportingUnit: extrusionReportingUnitHandle(
              editedSlabFloorExtrusion,
              roomRegions,
              floor.docEntityHandle,
            ),
          }),
        );
      }
    }
  }

  const roomRegionsByLevel = new Map<string, FloorRoomRegion[]>();
  for (const assemblyKey in slabFloorAssemblies) {
    const slabFloorAssembly = slabFloorAssemblies[assemblyKey];
    const { slab: slabFloorExtrusions } = slabFloorAssembly;
    const editedSlabFloorExtrusions: Extrusion[] = processFloorExtrusions(slabFloorExtrusions);

    let roomRegions = roomRegionsByLevel.get(slabFloorAssembly.levelId);
    if (!roomRegions) {
      roomRegions = floorRoomRegions(slabFloorAssembly.levelId, rooms, roomsByLevel);
      roomRegionsByLevel.set(slabFloorAssembly.levelId, roomRegions);
    }

    const bayHandle = bayHandleFromAssembly(slabFloorAssembly, floors);
    const pickHandle = horizontalAssemblyPickHandle(slabFloorAssembly, bayHandle, slabFloorAssembly.floorSystemId);

    editedSlabFloorExtrusions.forEach((editedSlabFloorExtrusion) =>
      emitter.emit({
        geom: extrusionToBRep(editedSlabFloorExtrusion),
        faceCategory: FaceCategory.SLAB_FLOORING,
        threeLayers: buildExteriorLayers(),
        levelId: slabFloorAssembly.levelId,
        materialSlot: "slabfloor",
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        pickHandle,
        entityHandleForSelectingReportingUnit: extrusionReportingUnitHandle(
          editedSlabFloorExtrusion,
          roomRegions,
          slabFloorAssembly.docEntityHandle,
        ),
      }),
    );
  }
}

//adhoc tolerance to hide subfloor extruded faces
const SUBFLOOR_EXTRUDED_FACE_HIDING_TOLERANCE = 0.05;

export function getSubFlooringGeometries(
  floorSystems: Table<FloorSystem>,
  framedFloorAssemblies: Table<GeneratedFramedFloorAssembly>,
  slabFloorAssemblies: Table<GeneratedSlabFloorAssembly>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  emitter: BuildingGeometryEmitter,
  floors?: Table<FloorAssemblyOverride>,
): void {
  const materialSrc = {
    ...MS_Color__C,
    colorId: DMC.SystemColors.Orange,
  };

  const roomRegionsByLevel = new Map<string, FloorRoomRegion[]>();
  const roomRegionsForLevel = (levelId: string): FloorRoomRegion[] => {
    let roomRegions = roomRegionsByLevel.get(levelId);
    if (!roomRegions) {
      roomRegions = floorRoomRegions(levelId, rooms, roomsByLevel);
      roomRegionsByLevel.set(levelId, roomRegions);
    }
    return roomRegions;
  };

  for (const levelId in floorSystems) {
    const roomRegions = roomRegionsForLevel(levelId);
    const floorSystem = floorSystems[levelId];
    for (const roomClassFloors of floorSystem.roomClassFloors) {
      for (const floor of roomClassFloors) {
        if (floor.floorType === FloorType.NONE) {
          continue;
        }
        const { subFloor: subFloorExtrusions } = floor;
        for (const subFloorExtrusion of subFloorExtrusions) {
          const updateSubFloorLoops: PolyLine2[] = [];
          for (let loopIndex = 0; loopIndex < subFloorExtrusion.profile.loops.length; loopIndex++) {
            const orientedLoop =
              loopIndex === 0
                ? polyLine2SetCW(
                    offsetPolyLine2(
                      subFloorExtrusion.profile.loops[loopIndex],
                      SUBFLOOR_EXTRUDED_FACE_HIDING_TOLERANCE,
                    ),
                  )
                : polyLine2SetCCW(
                    offsetPolyLine2(
                      subFloorExtrusion.profile.loops[loopIndex],
                      SUBFLOOR_EXTRUDED_FACE_HIDING_TOLERANCE,
                    ),
                  );
            updateSubFloorLoops.push(orientedLoop);
          }
          const editedSubFloorExtrusion: Extrusion = {
            ...subFloorExtrusion,
            profile: { ...subFloorExtrusion.profile, loops: updateSubFloorLoops },
          };

          emitter.emit({
            geom: extrusionToBRep(editedSubFloorExtrusion),
            faceCategory: FaceCategory.SUB_FLOORING,
            threeLayers: buildInteriorLayers(),
            levelId,
            materialSlot: "subfloor",
            materialSrc,
            uvGen: UVGeneration.GLOBAL_RECT_UV,
            pickHandle: floorExtrusionPickHandle(editedSubFloorExtrusion, roomRegions, floor),
            entityHandleForSelectingReportingUnit: extrusionReportingUnitHandle(
              editedSubFloorExtrusion,
              roomRegions,
              floor.docEntityHandle,
            ),
          });
        }
      }
    }
  }
  for (const assemblyKey in framedFloorAssemblies) {
    const framedFloorAssembly = framedFloorAssemblies[assemblyKey];
    const { subfloor: subFloorExtrusions } = framedFloorAssembly;
    const editedSubFloorExtrusions: Extrusion[] = processFloorExtrusions(subFloorExtrusions);

    const roomRegions = roomRegionsForLevel(framedFloorAssembly.levelId);
    const bayHandle = bayHandleFromAssembly(framedFloorAssembly, floors);
    const pickHandle = horizontalAssemblyPickHandle(framedFloorAssembly, bayHandle, framedFloorAssembly.floorSystemId);

    editedSubFloorExtrusions.forEach((editedSubFloorExtrusion) =>
      emitter.emit({
        geom: extrusionToBRep(editedSubFloorExtrusion),
        faceCategory: FaceCategory.SUB_FLOORING,
        threeLayers: buildExteriorLayers(),
        levelId: framedFloorAssembly.levelId,
        materialSlot: "subfloor",
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        pickHandle,
        entityHandleForSelectingReportingUnit: extrusionReportingUnitHandle(
          editedSubFloorExtrusion,
          roomRegions,
          framedFloorAssembly.docEntityHandle,
        ),
      }),
    );
  }
  for (const assemblyKey in slabFloorAssemblies) {
    const slabFloorAssembly = slabFloorAssemblies[assemblyKey];
    const { subfloor: subFloorExtrusions } = slabFloorAssembly;
    const editedSubFloorExtrusions: Extrusion[] = processFloorExtrusions(subFloorExtrusions);

    const roomRegions = roomRegionsForLevel(slabFloorAssembly.levelId);
    const bayHandle = bayHandleFromAssembly(slabFloorAssembly, floors);
    const pickHandle = horizontalAssemblyPickHandle(slabFloorAssembly, bayHandle, slabFloorAssembly.floorSystemId);

    editedSubFloorExtrusions.forEach((editedSubFloorExtrusion) =>
      emitter.emit({
        geom: extrusionToBRep(editedSubFloorExtrusion),
        faceCategory: FaceCategory.SUB_FLOORING,
        threeLayers: buildExteriorLayers(),
        levelId: slabFloorAssembly.levelId,
        materialSlot: "subfloor",
        materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        pickHandle,
        entityHandleForSelectingReportingUnit: extrusionReportingUnitHandle(
          editedSubFloorExtrusion,
          roomRegions,
          slabFloorAssembly.docEntityHandle,
        ),
      }),
    );
  }
}
